/**
 * View preferences + saved-view schemas — Phase 12 (M10).
 *
 * Backs:
 *   - chaos.user_view_preferences (per-user, per-board view + free-form
 *     settings). The gateway upserts on every change and returns the
 *     stored shape verbatim.
 *   - SavedViews inside List view (subset of view_settings_jsonb).
 *
 * Validation philosophy
 * =====================
 * The DB only stores `view_type` as a constrained enum and
 * `view_settings_jsonb` as an opaque JSONB. The Zod schema validates
 * the union of the well-known shapes (List / Gantt) at the API
 * boundary; unknown extra keys are allowed via `passthrough()` so a
 * future view kind can land without a coordinated migration.
 */
import { z } from 'zod';

import { autonomyLevelSchema } from './kanban';

// ── View type enum ──────────────────────────────────────────────────
export const viewTypes = ['kanban', 'list', 'gantt'] as const;
export const viewTypeSchema = z.enum(viewTypes);
export type ViewType = z.infer<typeof viewTypeSchema>;

// ── List view filters / sort / grouping ──────────────────────────────
// These mirror the in-memory shapes in
// `packages/chaos-ui-components/src/components/views/_shared/types.ts`
// but are the wire shape — kept ASCII-only camelCase for the API.
// 2026-08-02: that package was deleted (imported by nothing, ever). The file it
// names is readable at `git show c9a09af:<path>`; nothing consumes these shapes
// from it any more, so this paragraph is provenance, not a sync obligation.
export const cardListSortDirSchema = z.enum(['asc', 'desc']);
export type CardListSortDir = z.infer<typeof cardListSortDirSchema>;

export const cardListColumnIdSchema = z.enum([
  'title',
  'status',
  'agent',
  'autonomy',
  'progress',
  'due',
  'last_activity',
  'priority',
  'members',
  'cost_today',
]);
export type CardListColumnId = z.infer<typeof cardListColumnIdSchema>;

export const cardListSortSchema = z.object({
  column: cardListColumnIdSchema,
  dir: cardListSortDirSchema,
});
export type CardListSort = z.infer<typeof cardListSortSchema>;

export const cardListGroupingSchema = z.object({
  by: z.union([cardListColumnIdSchema, z.enum(['department', 'project']), z.null()]).default(null),
});
export type CardListGrouping = z.infer<typeof cardListGroupingSchema>;

export const cardListFiltersSchema = z
  .object({
    status: z
      .array(
        z.enum([
          'in_progress',
          'awaiting_review',
          'approval_pending',
          'blocked',
          'done',
          'archived',
        ]),
      )
      .optional(),
    autonomy: z.array(autonomyLevelSchema).optional(),
    agent: z.array(z.string()).optional(),
    /** Inclusive ISO day */
    due_after: z.string().optional(),
    due_before: z.string().optional(),
    search: z.string().optional(),
    /** Multi-column sort applied client-side after the gateway returns
     *  rows. The first array entry is the primary sort. */
    sorts: z.array(cardListSortSchema).optional(),
  })
  .passthrough();
export type CardListFilters = z.infer<typeof cardListFiltersSchema>;

// ── Saved view (subset of view_settings_jsonb) ──────────────────────
export const savedListViewSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  filters: cardListFiltersSchema,
  sort: cardListSortSchema.nullable(),
  grouping: cardListGroupingSchema,
  visible_columns: z.array(cardListColumnIdSchema),
  created_at: z.string(),
  shared_with: z.enum(['me', 'workspace']).default('me'),
});
export type SavedListView = z.infer<typeof savedListViewSchema>;

// ── Gantt view settings ──────────────────────────────────────────────
export const ganttScaleSchema = z.enum(['day', 'week', 'month']);
export type GanttScale = z.infer<typeof ganttScaleSchema>;

export const ganttViewSettingsSchema = z
  .object({
    scale: ganttScaleSchema.default('week'),
    showCriticalPath: z.boolean().default(false),
  })
  .passthrough();
export type GanttViewSettings = z.infer<typeof ganttViewSettingsSchema>;

// ── View settings union (per-view free-form blob) ───────────────────
export const listViewSettingsSchema = z
  .object({
    filters: cardListFiltersSchema.optional(),
    sort: cardListSortSchema.nullable().optional(),
    grouping: cardListGroupingSchema.optional(),
    visibleColumns: z.array(cardListColumnIdSchema).optional(),
    savedViews: z.array(savedListViewSchema).optional(),
    activeSavedViewId: z.string().nullable().optional(),
  })
  .passthrough();
export type ListViewSettings = z.infer<typeof listViewSettingsSchema>;

// ── user_view_preferences row ────────────────────────────────────────
export const viewPreferenceSchema = z.object({
  boardId: z.string().uuid(),
  viewType: viewTypeSchema,
  viewSettings: z.record(z.unknown()).default({}),
  updatedAt: z.string().datetime(),
});
export type ViewPreference = z.infer<typeof viewPreferenceSchema>;

export const upsertViewPreferenceSchema = z.object({
  viewType: viewTypeSchema,
  viewSettings: z.record(z.unknown()).optional(),
});
export type UpsertViewPreferenceInput = z.infer<typeof upsertViewPreferenceSchema>;

// ── Card dependency schemas ──────────────────────────────────────────
export const dependencyTypeSchema = z.enum(['FS', 'SS', 'FF', 'SF']);
export type DependencyType = z.infer<typeof dependencyTypeSchema>;

export const cardDependencySchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  cardId: z.string().uuid(),
  dependsOnCardId: z.string().uuid(),
  dependencyType: dependencyTypeSchema,
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime(),
});
export type CardDependency = z.infer<typeof cardDependencySchema>;

export const createCardDependencySchema = z.object({
  cardId: z.string().uuid(),
  dependsOnCardId: z.string().uuid(),
  dependencyType: dependencyTypeSchema.optional(),
});
export type CreateCardDependencyInput = z.infer<typeof createCardDependencySchema>;

// ── Bulk operations ──────────────────────────────────────────────────
export const bulkActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('move'),
    cardIds: z.array(z.string().uuid()).min(1).max(500),
    columnId: z.string().uuid(),
  }),
  z.object({
    action: z.literal('archive'),
    cardIds: z.array(z.string().uuid()).min(1).max(500),
  }),
  z.object({
    action: z.literal('assignAgent'),
    cardIds: z.array(z.string().uuid()).min(1).max(500),
    primaryAgentId: z.string().uuid().nullable(),
  }),
  z.object({
    action: z.literal('setAutonomy'),
    cardIds: z.array(z.string().uuid()).min(1).max(500),
    autonomyLevel: autonomyLevelSchema.nullable(),
  }),
]);
export type BulkAction = z.infer<typeof bulkActionSchema>;

export const bulkActionResultSchema = z.object({
  action: z.enum(['move', 'archive', 'assignAgent', 'setAutonomy']),
  updatedCount: z.number().int().nonnegative(),
  failedIds: z.array(z.string().uuid()),
});
export type BulkActionResult = z.infer<typeof bulkActionResultSchema>;
